import clsx from 'clsx'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Table from '../../../../UI/Table'
import Search from '../../../../UI/Search'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { branchField, companyStatusField, companyTypeField } from '../../../../UI/FilterPanel/fields'
import BtnFilter from '../../../../UI/BtnFilter'
import BtnToggle from '../../../../UI/BtnToggle'
import BtnTooltip from '../../../../UI/BtnTooltip'
import MiniHeader from '../../../../UI/MiniHeader'
import Pagination from '../../../../UI/Pagination'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { customNumberFormat } from '../../../../shared/helpers'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'

import styles from './style.module.scss'

const statusClassName = {
	false: styles.statusVerification,
	true: styles.statusActive
}

function CompaniesList(props) {
	const { t, i18n } = useTranslation()
	const history = useHistory()
	const [companies, setCompanies] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [assigned, setAssigned] = useState(true)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	const [search, setSearch] = useState('')
	const limit = 10
	const [showFilter, setShowFilter] = useState(false)

	const fields = useMemo(
		() => [companyTypeField(t), companyStatusField(t), branchField(t, props.branch, i18n.language)],
		[t, i18n.language, props.branch]
	)

	const filters = useFilters({ storageKey: 'companies-teacher', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchAllCompanies = () => {
		const isCurrent = latest()

		http.get(assigned ? TApi.TEACHER_GET_ASSIGNED_COMPANIES : TApi.TEACHER_GET_ALL_COMPANIES, {
			params: {
				limit,
				offset: page * limit,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			setCompanies(assigned ? data.supervisedCompanies : data.allCompanies)
			setCountAll(data.count)
		})
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchAllCompanies()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, assigned, sort, debouncedSearch, filterQuery])

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearch = e => {
		setPage(0)
		setSearch(e.target.value)
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	/*
	 * Das Profil ist seit 23.08.2026 eine eigene Seite und kein Fenster mehr:
	 * Es zeigt dieselben acht Abschnitte wie das Firmenprofil der Verwaltung,
	 * und die passen in kein Fenster (siehe views/teacher/CompanyProfile).
	 */
	const openCompany = company => history.push(TRoutes.TEACHER_COMPANY(company.id))

	const assignRequest = id => {
		http.post(TApi.TEACHER_ASSIGN_REQUEST, { id: id })
			.then(() => {
				toast.success(t('Request sent successfully'))
			})
			.then(() => {
				fetchAllCompanies()
			})
	}

	const columns = useMemo(
		() => [
			{
				Header: () => {
					return <span className={styles.tableTitleCompanyName}>{t('Companies')}</span>
				},
				minWidth: 250,
				accessor: 'name',
				Cell: ({ row }) => {
					return (
						<div className={styles.name}>
							<span className={styles.companyName}>{row.values.name}</span>

							<BtnTooltip
								items={[
									{
										label: t('View profile'),
										onClick: () => openCompany(row.original),
										disabled: !assigned
									},
									// „Risikoanalyse" stand hier als zweiter Eintrag und
									// oeffnete ein eigenes Fenster. Sie ist jetzt ein
									// Abschnitt im Profil — wie bei der Verwaltung, und ein
									// Fenster weniger uebereinander.
									{
										label: t('Request to assign'),
										onClick: () => assignRequest(row.original.id),
										disabled:
											assigned ||
											row.original.Requests?.find(({ teacherId }) => teacherId === props.id) ||
											row.original.TeachersSupervisions?.find(
												({ teacherId }) => teacherId === props.id
											)
									}
								]}
							/>
						</div>
					)
				}
			},

			{
				Header: t('employees'),
				accessor: 'employeesCount',
				disableSortBy: true,
				Cell: ({ row }) => {
					return <span className={styles.emploees}>{row.values.employeesCount}</span>
				}
			},
			{
				Header: t('Company type'),
				accessor: 'companyType',
				Cell: ({ row }) => {
					return <span className={styles.companyType}>{row.values.companyType}</span>
				}
			},
			{
				Header: t('branch'),
				accessor: 'branch',
				Cell: ({ row }) => {
					return <span className={styles.industryType}>{t(row.values.branch)}</span>
				}
			},
			{
				// Eine Zahl statt zweier Werte nebeneinander („- | -"): die Summe der
				// offenen Betraege aus allen Rechnungen der Firma. Wie in der
				// Unternehmensliste der Verwaltung.
				Header: t('Balance'),
				Cell: ({ row }) => {
					const invoices = row.original.Invoice || []
					return (
						<span className={styles.industryType}>
							{invoices.length
								? customNumberFormat(invoices.reduce((sum, invoice) => sum + (invoice.balance || 0), 0))
								: '-'}
						</span>
					)
				}
			},
			{
				Header: 'Status',
				accessor: 'isActive',
				Cell: ({ row }) => {
					return (
						<div className={styles.optionBtnCell}>
							<span className={clsx(styles.status, statusClassName[row.values.isActive])}>
								{row.original.isActive ? t('Activated') : t('Not Activated')}
							</span>
						</div>
					)
				}
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assigned, t]
	)
	return (
		<div className={styles.root}>
			<MiniHeader className={styles.headerContainer} title={t('companies')}>
				<div className={styles.header}>
					<div className={styles.searchContainer}>
						<Search
							value={search}
							placeholder={t('Search companies...')}
							onChange={handleSearch}
							onSubmit={e => e.preventDefault()}
						/>
						<BtnFilter
							icon="arrow"
							onClick={() => setShowFilter(true)}
							count={filters.count}
						>
							{t('Filter')} ({filters.count})
						</BtnFilter>
					</div>
				</div>
			</MiniHeader>
			{/*
			 * Der Umschalter steht unter der Kopfzeile, nicht darin: In der Kopfzeile
			 * ueberlagerte er bei 1440 px den Seitentitel und den Hinweisreiter der
			 * Testumgebung (Anforderung #82). Dieselbe Zeile traegt die Blaetterung,
			 * wie in den uebrigen Listen.
			 */}
			<div className={styles.subHeader}>
				<div className={styles.toggleContainer}>
					<p className={styles.toggleLeftTitle}>{t('ASSIGNED COMPANIES')}</p>
					<BtnToggle
						onClick={() => {
							setAssigned(!assigned)
						}}
					/>
					<p className={styles.toggleRightTitle}>{t('All School Companies')}</p>
				</div>
				<Pagination limit={limit} page={page} label={t('companies')} countAll={countAll} onChange={setPage} />
			</div>
			<div className="container">
				<Table
					columns={columns}
					page={page}
					items={companies}
					limit={limit}
					countAll={countAll}
					onChange={setPage}
					handleSort={handleSort}
					// Nur betreute Companies haben ein Profil zu zeigen.
					onRowClick={openCompany}
					isRowClickable={() => Boolean(assigned)}
					rowTitle={t('View profile')}
				/>
			</div>
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	id: state.user.profile.id,
	branch: state.options.branch
})

export default connect(mapStateToProps)(CompaniesList)
